import { jwtDecode } from 'jwt-decode';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const CLIENT_TOKEN_KEY = 'clientToken';
const SESSION_EXPIRED_EVENT = 'firm-session-expired';
const REFRESH_EARLY_MS = 60 * 1000;
const REFRESH_RETRY_MS = 30 * 1000;

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const nativeFetch = typeof window !== 'undefined' ? window.fetch.bind(window) : null;

let refreshPromise = null;
let refreshTimer = null;
let interceptorInstalled = false;

function tokenExpiresAt(token) {
  if (!token) return null;

  try {
    const { exp } = jwtDecode(token);
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function tokenIsFresh(token, bufferMs = REFRESH_EARLY_MS) {
  const expiresAt = tokenExpiresAt(token);
  return Boolean(expiresAt && expiresAt - Date.now() > bufferMs);
}

function refreshUrl() {
  if (!API_BASE_URL) return '/api/token/refresh/';
  return /\/api$/i.test(API_BASE_URL)
    ? `${API_BASE_URL}/token/refresh/`
    : `${API_BASE_URL}/api/token/refresh/`;
}

function isApiRequest(url) {
  try {
    const requestUrl = new URL(url, window.location.origin);
    const apiUrl = new URL(API_BASE_URL || '/api', window.location.origin);
    const apiPath = apiUrl.pathname.replace(/\/$/, '');

    return requestUrl.origin === apiUrl.origin
      && (!apiPath || apiPath === '/' || requestUrl.pathname === apiPath || requestUrl.pathname.startsWith(`${apiPath}/`));
  } catch {
    return false;
  }
}

function bearerToken(headers) {
  const value = headers.get('Authorization');
  const match = value && value.match(/^Bearer\s+(.+)$/i);
  return match ? match[1] : null;
}

function isFirmRequest(request) {
  if (!isApiRequest(request.url) || request.url === refreshUrl()) return false;

  const sentToken = bearerToken(request.headers);
  if (!sentToken) return false;

  const clientToken = localStorage.getItem(CLIENT_TOKEN_KEY);
  if (clientToken && sentToken === clientToken) return false;

  return Boolean(
    localStorage.getItem(ACCESS_TOKEN_KEY)
    || localStorage.getItem(REFRESH_TOKEN_KEY)
  );
}

function dispatchSessionExpired() {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
}

function clearRefreshTimer() {
  if (refreshTimer) {
    window.clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleRefresh(token) {
  clearRefreshTimer();

  const expiresAt = tokenExpiresAt(token);
  if (!expiresAt || !localStorage.getItem(REFRESH_TOKEN_KEY)) return;

  const delay = Math.max(0, expiresAt - Date.now() - REFRESH_EARLY_MS);
  refreshTimer = window.setTimeout(async () => {
    try {
      await refreshFirmAccessToken();
    } catch {
      // Preserve the session during temporary network/server trouble and try again.
      if (localStorage.getItem(REFRESH_TOKEN_KEY)) {
        refreshTimer = window.setTimeout(() => scheduleRefresh(localStorage.getItem(ACCESS_TOKEN_KEY)), REFRESH_RETRY_MS);
      }
    }
  }, delay);
}

export function storeFirmSession({ access, refresh }) {
  if (access) localStorage.setItem(ACCESS_TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  scheduleRefresh(access || localStorage.getItem(ACCESS_TOKEN_KEY));
}

export function clearFirmSession({ notify = false } = {}) {
  clearRefreshTimer();
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  if (notify) dispatchSessionExpired();
}

async function performRefresh() {
  const refresh = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refresh || !nativeFetch) return null;

  let response;
  try {
    response = await nativeFetch(refreshUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    });
  } catch (error) {
    // A temporary connection failure should not erase a still-usable session.
    throw error;
  }

  if (!response.ok) {
    if ([400, 401, 403].includes(response.status)) {
      clearFirmSession({ notify: true });
      return null;
    }
    throw new Error(`Unable to refresh the firm session (${response.status}).`);
  }

  const data = await response.json();
  if (!data?.access) {
    throw new Error('The refresh response did not include an access token.');
  }

  storeFirmSession({ access: data.access, refresh: data.refresh });
  return data.access;
}

export function refreshFirmAccessToken() {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function getValidFirmAccessToken() {
  const access = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (tokenIsFresh(access)) return access;

  if (!localStorage.getItem(REFRESH_TOKEN_KEY)) return null;
  return refreshFirmAccessToken();
}

async function firmFetch(input, init) {
  let request;
  try {
    request = new Request(input, init);
  } catch {
    return nativeFetch(input, init);
  }

  if (!isFirmRequest(request)) return nativeFetch(request);

  let access;
  try {
    access = await getValidFirmAccessToken();
  } catch {
    // Let the original request proceed during temporary refresh trouble.
    access = localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  const headers = new Headers(request.headers);
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const authenticatedRequest = new Request(request, { headers });
  const retrySource = authenticatedRequest.clone();
  const response = await nativeFetch(authenticatedRequest);

  if (response.status !== 401 || !localStorage.getItem(REFRESH_TOKEN_KEY)) {
    return response;
  }

  let refreshedAccess;
  try {
    refreshedAccess = await refreshFirmAccessToken();
  } catch {
    return response;
  }

  if (!refreshedAccess) return response;

  const retryHeaders = new Headers(retrySource.headers);
  retryHeaders.set('Authorization', `Bearer ${refreshedAccess}`);
  return nativeFetch(new Request(retrySource, { headers: retryHeaders }));
}

export function installFirmSession() {
  if (interceptorInstalled || !nativeFetch) return;
  interceptorInstalled = true;
  window.fetch = firmFetch;

  scheduleRefresh(localStorage.getItem(ACCESS_TOKEN_KEY));

  window.addEventListener('storage', (event) => {
    if (![ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY].includes(event.key)) return;

    if (!localStorage.getItem(REFRESH_TOKEN_KEY)) {
      clearRefreshTimer();
      dispatchSessionExpired();
      return;
    }

    scheduleRefresh(localStorage.getItem(ACCESS_TOKEN_KEY));
  });
}

export { SESSION_EXPIRED_EVENT };
